import { Api } from '@shared-providers/api.provider';
import { Injectable } from '@angular/core';
import { Provider } from '@shared-providers/Provider';
import { LoadingService } from '@shared-services/loading.service';
import { IClient, IClientHistory } from '@internal-models/client.model';
import { IDataQuery, ITableData } from '@beego/ngx-table';
import { encodeQueryUrl } from '@shared-libs/helpers';
import { IJob } from '@shared-models/job.model';

@Injectable({
	providedIn: 'root',
})
export class ClientProvider extends Provider<IClient> {
	protected readonly url: string = '/clients';

	constructor(
		protected readonly api: Api,
		protected readonly loadingService: LoadingService
	) {
		super(loadingService);
	}

	public async getAll(query: IDataQuery): Promise<ITableData<IClient>> {
		return this.process(
			this.api.get(`${this.url}?query=${encodeQueryUrl(query)}`),
			!query.offset
		) as Promise<ITableData>;
	}

	public async getOne(clientId: string): Promise<IClient> {
		return this.process(this.api.get(`${this.url}/${clientId}`)) as Promise<IClient>;
	}

	public async getDigichequeCount(clientId: string): Promise<number> {
		return this.process(this.api.get(`${this.url}/digicheques/${clientId}`, true)) as Promise<number>;
	}

	public async getHistory(clientId: string, query: IDataQuery): Promise<ITableData<IClientHistory>> {
		return this.process(
			this.api.get(`${this.url}/history/${clientId}?query=${encodeQueryUrl(query)}`)
		) as Promise<ITableData>;
	}

	public getClientsExport(): Promise<any> {
		return this.process(this.api.download(`${this.url}/export`, `Bezoekers_Export.xlsx`));
	}

	public startImportClientsValidationAndFormatting(form: FormData): Promise<IJob> {
		return this.process(this.api.post(`${this.url}/import/excel`, form)) as any as Promise<IJob>;
	}

	public importClients(Clients: Array<IClient>): Promise<Array<IClient>> {
		return this.process(this.api.post(`${this.url}/import/clients`, { Clients })) as any as Promise<Array<IClient>>;
	}

	public getImportTemplate(): Promise<any> {
		return this.process(this.api.download(`${this.url}/import/template`, 'template_import_bezoekers.xlsx'));
	}

	public searchAll(searchParams: Pick<IClient, 'firstname' | 'lastname' | 'birthDate'>): Promise<Array<IClient>> {
		return this.process(this.api.post(`${this.url}/search`, searchParams), false) as Promise<Array<IClient>>;
	}

	public create(client: Partial<IClient>): Promise<IClient> {
		return this.process(this.api.post(`${this.url}`, client)) as Promise<IClient>;
	}

	public update(clientId: string, client: Partial<IClient>): Promise<IClient> {
		return this.process(this.api.put(`${this.url}/${clientId}`, client)) as Promise<IClient>;
	}

	public mergeClients(data: { originClientId: string; targetClientId: string }): Promise<void> {
		return this.process(this.api.post(`${this.url}/merge`, data)) as any as Promise<void>;
	}

	public delete(clientId: string): Promise<IClient> {
		return this.process(this.api.delete(`${this.url}/${clientId}`)) as Promise<IClient>;
	}
}
